import React, { useEffect, useState } from 'react'
import { Button, Cascader, Checkbox, Form, InputNumber, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { BasicSettingWrapper, CustomSettingWrapper, FormButton, SettingWrapper } from '../dashboard.styled'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import Filter from './Filter'
import CrossDataSetting from './CrossData'
import { fetchSources } from '../../../apis/source'
import { Sources } from '../../../constants/Sources'

export function TimelineSetting (props) {
  const { defPlaceholder, defConfig, sortOptions } = props

  return (
    <>
      <Form.Item {...defConfig('order', 'select')}>
        <Select showSearch placeholder={defPlaceholder('order', 'select')} options={sortOptions} optionFilterProp="children" />
      </Form.Item>
      <Form.Item {...defConfig('limit', 'input', false)}>
        <InputNumber placeholder={defPlaceholder('limit', 'input')} precision={0} min={0} style={{ width: '100%' }} />
      </Form.Item>
    </>
  )
}

export function TimelineExtraSetting (props) {
  const { defPlaceholder, defConfig, dataSourceTree, dataSourceTableMapping, handleModalClose, loading, form, tableJoinKeyMapping, columnMapping, editData, show } = props
  const { t: ln_t } = useTranslation()
  const [dataSources, setDataSources] = useState({})
  const [sourceCount, setSourceCount] = useState([1])
  const [filterCounter, setFilterCounter] = useState({})
  const [crossDataChecked, setCrossDataChecked] = useState({})
  const [crossDataCount, setCrossDataCount] = useState({})
  const [crossDataSchema, setCrossDataSchema] = useState({})
  const formVal = Form.useWatch() || {}
  const dataSourceDetails = dataSourceTree?.reduce((all, i) => ({ ...all, [i.dataSourceId]: i }), {})

  const sourceFields = ['dataSourceId', 'tableName', 'time', 'timeFormat', 'content']
  const filterFields = ['column', 'operator', 'value']
  const handleAddSource = () => setSourceCount([...sourceCount, Math.max(...sourceCount) + 1])
  const handleDelSource = source => {
    const filterFormKeys = filterFields?.map(field => filterCounter[source]?.map(num => `${source}-${num}-${field}`)).flat()
    form.setFieldsValue({
      ...sourceFields.reduce((all, field) => ({ ...all, [`${source}-${field}`]: undefined }), {}),
      ...filterFormKeys.reduce((all, formKey) => ({ ...all, [formKey]: undefined }), {})
    })
    setSourceCount(sourceCount.filter(i => i !== source))
    setFilterCounter({ ...filterCounter, [source]: [] })
    setCrossDataChecked({ ...crossDataChecked, [source]: false })
    setCrossDataCount({ ...crossDataCount, [source]: [1] })
  }
  const resetFields = (source, excludeTableName) => {
    const fieldsToReset = Object.keys(formVal).filter(i => {
      const [sourceNum, field] = i.split('-')
      return Number(sourceNum) === source && field !== 'dataSourceId' && field !== 'predefineOpt' && field !== 'filterLogic' && (!excludeTableName || field !== 'tableName')
    })
    form.setFieldsValue(fieldsToReset?.reduce((all, field) => ({ ...all, [field]: undefined }), {}))
  }

  const handleCheckCrossData = (e, num) => {
    setCrossDataChecked({ ...crossDataChecked, [num]: e.target.checked })
    resetFields(num)
    form.setFieldsValue({ [`${num}-dataSourceId`]: undefined, [`${num}-predefineOpt`]: 'custom' })
  }

  const handleAddFilter = source => {
    const newFilterNum = Math.max(...filterCounter[source]?.length ? filterCounter[source] : [0]) + 1
    setFilterCounter({ ...filterCounter, [source]: [...filterCounter[source] || [], newFilterNum] })
  }
  const handleDelFilter = (source, key) => {
    setFilterCounter(prev => ({ ...prev, [source]: prev[source]?.filter(i => i !== key) }))
    form.setFieldsValue({ [`${source}-${key}-column`]: undefined, [`${source}-${key}-value`]: undefined, [`${source}-${key}-operator`]: undefined })
  }

  const dataSourceIsSite = id => dataSources[dataSourceDetails[id]?.dataSourceType] === Sources.SITE
  const handleChangeDataSource = (e, num) => {
    resetFields(num, false)
    form.setFieldValue(`${num}-predefineOpt`, !dataSourceIsSite(e) ? 'custom' : undefined)
  }
  const handleTableChange = (e, num) => {
    resetFields(num, true)
    if (dataSourceIsSite(formVal[`${num}-dataSourceId`])) form.setFieldValue(`${num}-predefineOpt`, e === 'visit' ? 'custom' : undefined)
  }

  const getColumnOpt = source => crossDataChecked[source] ? crossDataSchema[source] : tableJoinKeyMapping?.[`${formVal?.[`${source}-dataSourceId`]}-${formVal?.[`${source}-tableName`]}`] || []
  const getFilterColMapping = source => {
    const uniq = `${formVal[`${source}-dataSourceId`]}^@^${formVal[`${source}-tableName`]}^@^`
    return Object.entries(columnMapping)?.filter(([key, val]) => key.startsWith(uniq)).reduce((all, [key, val]) => ({ ...all, [key.replace(uniq, '')]: val }), {})
  }

  const getCrossDataKeys = (source, fields) => (crossDataCount[source] || [1]).map(sequence => fields?.map(field => ([`${source}-${sequence}-parent-${field}`, `${source}-${sequence}-child-${field}`]))).flat(Infinity)
  const validateCrossData = async (source, fields = ['dataSource', 'tableName', 'joinKey']) => await form.validateFields(getCrossDataKeys(source, fields))

  const getCrossDataColumns = async source => {
    await validateCrossData(source)
    const dataSourcesName = dataSourceTree?.map(i => ({ key: i.dataSourceId, val: i.dataSourceName }))?.reduce((all, { key, val }) => ({ ...all, [key]: val }), {}) || {}
    const mapping = {}
    getCrossDataKeys(source, ['dataSource'])?.forEach(key => {
      const id = formVal[key]
      if (!mapping[id]) mapping[id] = { value: id, label: dataSourcesName[id], children: [] }
      mapping[id].children = [...mapping[id].children, { value: formVal[key?.replace('dataSource', 'tableName')] }]
    })
    const formattedSchema = Object.values(mapping)?.map(i => {
      const groupByTable = Object.groupBy(i.children, ({ value }) => value)
      const children = Object.entries(groupByTable)?.map(([key, val]) => ({ value: key, label: key, children: tableJoinKeyMapping[`${i.value}-${key}`] }))
      return { ...i, children }
    })
    setCrossDataSchema({ ...crossDataSchema, [source]: formattedSchema })
  }

  const getDataSourceType = async () => {
    const res = await fetchSources()
    if (res?.status === 200) setDataSources(res?.data?.reduce((all, i) => ({ ...all, [i.id]: i.name }), {}))
  }
  useEffect(() => {
    getDataSourceType()
  }, [])

  useEffect(() => {
    setSourceCount(editData?.UISetting?.sourceCount || [1])
    setFilterCounter(editData?.UISetting?.filterCounter || {})
    setCrossDataCount(editData?.UISetting?.crossDataCount || {})
    setCrossDataChecked(Object.keys(editData?.UISetting?.crossDataCount || {})?.reduce((all, i) => ({ ...all, [Number(i)]: true }), {}))
  }, [show])

  const predefineOpt = num => [
    ...formVal[`${num}-tableName`] === 'event' ? [{ value: 'site_event_click_label', label: ln_t('dashboard.eventclick') }] : [{ value: 'site_pageview_url', label: ln_t('dashboard.pageview') }],
    { value: 'custom', label: ln_t('dashboard.selectCustom') }
  ]
  const timeFormatOpt = [{ value: 'yyyy-MM-dd', label: 'yyyy-MM-dd' }, { value: 'timestamp', label: 'timestamp' }]

  const colDisabled = num => {
    if (crossDataChecked[num] && crossDataSchema[num]) return false
    const predefOpt = formVal[`${num}-predefineOpt`]
    if (predefOpt === 'site_pageview_url' || predefOpt === 'site_event_click_label') return true
    return !formVal[`${num}-tableName`] || (!formVal[`${num}-predefineOpt`] && dataSourceIsSite(num))
  }
  const defProps = source => ({ showSearch: true, disabled: colDisabled(source), options: getColumnOpt(source) })
  const SelectComponent = (source, props) => crossDataChecked[source] ? <Cascader {...defProps(source)} {...props} expandTrigger='hover' /> : <Select {...defProps(source)} {...props} optionFilterProp='children' />

  return <>
    {sourceCount?.map(num => <div key={num} style={{ borderTop: '1px solid rgba(0,0,0,.1)', marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
        <Checkbox checked={crossDataChecked?.[num]} onChange={e => handleCheckCrossData(e, num)}>
          {ln_t('dashboard.crossDataCalculation')}
        </Checkbox>
        {sourceCount.length !== 1 &&
          <Button type="link" icon={<DeleteOutlined />} style={{ color: 'var(--primary', marginLeft: 'auto' }} onClick={() => handleDelSource(num)}>
            {ln_t('util.delete')}
          </Button>}
      </div>

      <SettingWrapper>
        <BasicSettingWrapper style={{ paddingTop: '8px', paddingLeft: 0 }}>
          {!crossDataChecked[num] && <>
            <Form.Item {...defConfig('dataSource', 'select')} name={`${num}-dataSourceId`}>
              <Select
                showSearch
                placeholder={defPlaceholder('dataSource', 'select')}
                options={dataSourceTree?.map(i => ({ value: i.dataSourceId, label: i.dataSourceName }))}
                optionFilterProp="children"
                onChange={e => handleChangeDataSource(e, num)}
              />
            </Form.Item>
            <Form.Item {...defConfig('tableName', 'select')} name={`${num}-tableName`}>
              <Select
                showSearch
                placeholder={defPlaceholder('tableName', 'select')}
                options={dataSourceTableMapping?.[formVal?.[`${num}-dataSourceId`]]}
                optionFilterProp="children"
                disabled={!formVal[`${num}-dataSourceId`]}
                onChange={e => handleTableChange(e, num)}
              />
            </Form.Item>
          </>}
        </BasicSettingWrapper>

        <CustomSettingWrapper style={{ paddingTop: '8px', paddingRight: 0 }}>
          {crossDataChecked[num] &&
            <CrossDataSetting
            {...props} hidePreview
            onRelate={() => getCrossDataColumns(num)}
            count={crossDataCount[num] || [1]}
            setCount={e => setCrossDataCount({ ...crossDataCount, [num]: e })}
            validateCrossData={e => validateCrossData(num, e)}
            uniqueKey={num}
          />}

          <Form.Item {...defConfig('predefineOpt', 'input')} name={`${num}-predefineOpt`}>
            <Select
              showSearch
              placeholder={defPlaceholder('predefineOpt', 'select')}
              options={predefineOpt(num)}
              optionFilterProp="children"
              disabled={!formVal[`${num}-dataSourceId`] || !dataSourceIsSite(formVal[`${num}-dataSourceId`]) || (dataSourceIsSite(formVal[`${num}-dataSourceId`]) && formVal[`${num}-tableName`] === 'visit')}
            />
          </Form.Item>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Form.Item {...defConfig('time', 'input', !colDisabled(num))} style={{ width: '100%' }} name={`${num}-time`}>
              {SelectComponent(num, { placeholder: defPlaceholder('time', 'select') })}
            </Form.Item>
            <Form.Item {...defConfig('timeFormat', 'input', !colDisabled(num))} style={{ width: '100%' }} name={`${num}-timeFormat`}>
              <Select
                showSearch
                placeholder={defPlaceholder('timeFormat', 'select')}
                options={timeFormatOpt}
                optionFilterProp="children"
                disabled={colDisabled(num)}
              />
            </Form.Item>
          </div>
          <Form.Item {...defConfig('content', 'input', !colDisabled(num))} name={`${num}-content`}>
            {SelectComponent(num, { placeholder: defPlaceholder('time', 'select') })}
          </Form.Item>

          <Filter
            {...props}
            uniqueKey={num}
            filterOpt={getColumnOpt(num)}
            counter={filterCounter[num] || []}
            handleDelCounter={(...args) => handleDelFilter(num, ...args)}
            handleAddCounter={() => handleAddFilter(num)}
            columnMapping={crossDataChecked[num] ? columnMapping : getFilterColMapping(num)}
            crossData={crossDataChecked[num]}
          />
        </CustomSettingWrapper>
      </SettingWrapper>
    </div>)}

    <FormButton>
      <Button type="default" icon={<PlusOutlined />} onClick={handleAddSource}>{ln_t('dashboard.addSource')}</Button>
      <Button type="default" onClick={() => handleModalClose()} style={{ marginLeft: 'auto' }}>{ln_t('util.cancel')}</Button>
      <Button type="primary" htmlType="submit" loading={loading}>{ln_t('util.confirm')}</Button>
    </FormButton>
  </>
}
